import { TableRow, Box } from '@mui/material';
import { useMemo } from 'react';
import { useAppDispatch } from '~/store';
import { setDownloadList } from '~/store/resource';
import { FileStatus } from '~/store/resource/resource.type';
import { TDownloadableResource } from '~/types/common.type';
import { SelectType, UpdateStatus } from '~/types/enum';
import { isWeb } from '~/utils/helper/device';
import { fileSizeFormat } from '~/utils/helper/format';
import { getDirPath, getFileUpdateStatus } from '~/utils/helper/function';
import { useAutoExtractEffect } from '~/utils/hook/useAutoExtractEffect';
import { useDownload, openMode } from '~/utils/hook/useDownload';
import {
  BaseButton,
  BASE_BUTTON_COLOR,
  BASE_BUTTON_VARIANT,
} from '~/components/Global/Button';
import { downloadTable } from '~/components/Global/Dialog/Dialog.style';
import { useFile } from '~/utils/hook/useFile';
import { downloadStartEvent } from '~/tracking';
import { useCommon } from '~/utils/hook/useCommon';

export type THandleSelectedList = {
  type: SelectType;
  data?: TDownloadableResource;
};

type TProps = {
  data: TDownloadableResource;
  isChecked: boolean;
  hasCheckBox: boolean;
  handleSelectedList: ({ type, data }: THandleSelectedList) => void;
  isOverallSelectable: boolean;
};

export const DownloadDialogItem = ({
  data,
  isChecked,
  handleSelectedList,
  hasCheckBox,
  isOverallSelectable,
}: TProps) => {
  const dispatch = useAppDispatch();
  const { download, downloadListChangeListener, update } = useDownload();
  const { openPath } = useFile();
  const { openExternal } = useCommon();
  // progress 由資源頁依 url 即時查好傳進來；不要再去目前頁碼的查詢結果裡找，
  // 載入下一頁後前面頁面的資源會找不到，被當成未下載（NBX3-1117）
  const { resource, progress } = data;
  const { fileName, file, displayName } = resource;

  const {
    status,
    key,
    updateTime,
    totalBytes,
    downloadedAt,
    progressRatio,
    installingProgressRatio,
  } = progress;
  const { fileSize, url, fileUploadTime } = file;
  const { unit, size } = fileSizeFormat(fileSize);
  const updateStatus = getFileUpdateStatus({
    uploadedAt: fileUploadTime,
    downloadedAt,
  });

  const hasProgress = useMemo(
    () =>
      status === FileStatus.progressing ||
      status === FileStatus.paused ||
      status === FileStatus.completed ||
      status === FileStatus.installing,
    [status],
  );

  const overallProgress = useMemo(
    () =>
      status === FileStatus.installing
        ? installingProgressRatio
        : progressRatio,
    [status, installingProgressRatio, progressRatio],
  );

  const handleAction = () => {
    downloadStartEvent({
      event_type: '彈窗單檔下載',
      edu: resource.eduCodes[0],
      subject: resource.subjects[0],
      category: resource.resources1[0],
      resources_label: resource.resources2[0],
      resources_sub_label: resource.resources3[0],
      resources_url: resource.file.url,
      resources_name: resource.fileName,
    });
    if (isWeb) {
      void download({ resource, mode: openMode.link });
    } else {
      downloadListChangeListener(({ downloadList }) => {
        dispatch(setDownloadList(downloadList));
      });
      if (status === FileStatus.non || status === FileStatus.cancel) {
        void download({ resource, mode: openMode.link });
      } else if (status === FileStatus.finish) {
        if (!key) return;
        const dirPath = getDirPath({ key, fileName });
        openPath(dirPath);
      }
    }
  };

  const handleUpdate = () => {
    if (!key) return;
    update({
      filePath: key,
      filename: fileName,
      resource,
      mode: openMode.link,
    });
  };

  useAutoExtractEffect({ status, key, fileName, url, updateTime, totalBytes });

  const handlePreview = () => {
    const existingMatching: Record<string, string> = JSON.parse(
      localStorage.getItem('pdf_key_matching') || '{}',
    ) as Record<string, string>;
    const newMatching: Record<string, string> = {
      ...existingMatching,
      [resource._id]: url,
    };
    localStorage.setItem('pdf_key_matching', JSON.stringify(newMatching));

    openExternal(
      `${import.meta.env.VITE_ONEBOX_DOMAIN}/pdf/viewer.html?key=${resource._id}`,
    );
  };

  return (
    <TableRow
      key={Math.random()}
      sx={{ backgroundColor: isChecked ? '#E3F0FD' : '' }}
      hover={false}
    >
      {isOverallSelectable && (
        <downloadTable.cell width="0px">
          {hasCheckBox && (
            <downloadTable.checkbox
              checked={isChecked}
              onChange={(_, checked: boolean) => {
                checked
                  ? handleSelectedList({
                      type: SelectType.add,
                      data,
                    })
                  : handleSelectedList({
                      type: SelectType.clear,
                      data,
                    });
              }}
            />
          )}
        </downloadTable.cell>
      )}
      <downloadTable.cell width="100%">{displayName}</downloadTable.cell>
      <downloadTable.cell width="120px">
        {size}
        {unit}
      </downloadTable.cell>
      <downloadTable.cell width="230px">
        <Box
          sx={{
            display: 'flex',
            justifyContent:
              updateStatus === UpdateStatus.old ? 'space-between' : 'flex-end',
          }}
        >
          {updateStatus === UpdateStatus.old && (
            <BaseButton
              color={BASE_BUTTON_COLOR.SECONDARY}
              variant={BASE_BUTTON_VARIANT.GHOST}
              text="資源更新"
              onClick={handleUpdate}
            />
          )}
          {(isWeb ||
            status === FileStatus.non ||
            status === FileStatus.cancel) && (
            <>
              {(fileName.includes('.pdf') || fileName.includes('.PDF')) && (
                <BaseButton
                  color={BASE_BUTTON_COLOR.SECONDARY}
                  variant={BASE_BUTTON_VARIANT.GHOST}
                  text="預覽"
                  onClick={handlePreview}
                  style={{ marginRight: '8px' }}
                  width="96px"
                />
              )}
              <BaseButton
                variant={BASE_BUTTON_VARIANT.GHOST}
                text="下載"
                color={BASE_BUTTON_COLOR.PRIMARY}
                width="96px"
                onClick={handleAction}
              />
            </>
          )}
          {status === FileStatus.finish && (
            <BaseButton
              variant={BASE_BUTTON_VARIANT.GHOST}
              text="開啟"
              color={BASE_BUTTON_COLOR.SUCCESS}
              width="96px"
              onClick={handleAction}
            />
          )}
          {hasProgress && (
            <BaseButton
              text={`${overallProgress}%`}
              variant={BASE_BUTTON_VARIANT.GHOST}
              color={BASE_BUTTON_COLOR.PRIMARY}
              width="96px"
            />
          )}
        </Box>
      </downloadTable.cell>
    </TableRow>
  );
};
